import { ColumnDef, sortingFns } from '@tanstack/react-table';
import React, { useMemo } from 'react';
import { GTColumnSelectAnchor, GTTdm } from 'src/components/GenericTable';
import { filter } from 'lodash';
import { GTInternalIds } from 'src/components/GenericTable/types';
import { TableUserItem } from 'src/common';
import useLocalStorage from 'src/hooks/useLocalStorage';
import { TableIds } from 'src/api/types/misc';
import { useAcceptRequestMutation, useRejectRequestMutation, WorkspaceMembersQuery } from 'src/graphql/_generated';
import { useCache } from 'src/graphql/useCache';

type Request = NonNullable<WorkspaceMembersQuery['workspace']>['requests'][0];

type UseColumns = (a: { defaultColumns?: string[]; workspace?: NonNullable<WorkspaceMembersQuery['workspace']> }) => {
  columns: ColumnDef<Request>[];
  GTColumnSelectAnchorExported: JSX.Element;
};

const defaultColumns = ['name', 'email'];

export const useRequestsColumns: UseColumns = ({ workspace }) => {
  const [selectedColumns, setSelectedColumns] = useLocalStorage(`${TableIds.wsRequests}-columns`, defaultColumns);
  const { refetchQuery } = useCache();

  const resetCache = () => refetchQuery({ include: ['workspaceMembers'] });

  const [acceptRequest] = useAcceptRequestMutation({ onCompleted: resetCache });
  const [rejectRequest] = useRejectRequestMutation({ onCompleted: resetCache });

  const columns = useMemo(() => {
    const internalIds = [GTInternalIds.tdm];

    const list: ColumnDef<Request>[] = [
      {
        id: 'name',
        header: 'Name',
        accessorFn: (row) => row.name,
        cell: ({ row }) => {
          return <TableUserItem name={row.original.name || ''} />;
        },
        sortingFn: sortingFns.text,
      },
      {
        id: 'email',
        header: 'Email',
        accessorFn: (row) => row.email || null,
        cell: (info) => info.getValue(),
        sortingFn: sortingFns.text,
      },
      {
        id: GTInternalIds.tdm,
        header: () => null,
        cell: ({ row }) => {
          const requestId = row.original.request?.id!;
          const canSeeRequests = workspace?.actionsOnThisWorkspace?.canSeeRequests;

          const onAcceptRequestClick = canSeeRequests ? () => acceptRequest({ variables: { requestId } }) : undefined;
          const onRejectRequestClick = canSeeRequests ? () => rejectRequest({ variables: { requestId } }) : undefined;

          return (
            <div css="display: flex;grid-gap: 8px">
              <GTTdm onAcceptRequestClick={onAcceptRequestClick} onRejectRequestClick={onRejectRequestClick} />
            </div>
          );
        },
        size: 1,
      },
    ];

    return filter(list, (el) => [...internalIds, ...selectedColumns].includes(el.id as string));
  }, [workspace?.requests, selectedColumns]);

  const GTColumnSelectAnchorExported = (
    <GTColumnSelectAnchor
      tableId={TableIds.wsRequests}
      options={[
        { key: 'name', title: 'Name' },
        { key: 'email', title: 'Email' },
      ]}
      defaultOptions={defaultColumns}
      value={selectedColumns}
      onChange={setSelectedColumns}
    />
  );

  return {
    columns,
    GTColumnSelectAnchorExported,
  };
};
